
import { Component, Prop, Watch } from "vue-property-decorator";
import { BaseView } from "@components/base/view";
import { correlationService } from "@services/correlation.service";
import { isAfter, subMonths, endOfDay, subYears } from "date-fns";
import { chartService } from "@services/charts.service";

@Component({
  name: "ci-performance-correlation",
})
export default class extends BaseView {
  @Prop({ required: true }) isins!: string[];
  @Prop({ default: () => [] }) benchmarks!: Nullable<Portfolios.Benchmark[]>;

  public errorMessage = "";
  public calculating: boolean = false;
  public youngest: number = 0;
  public correlationData: ChartApi.CorrelationData[] = [];

  public selectedPeriod = "max";
  public hovering: { x: Nullable<number>; y: Nullable<number> } = {
    x: null,
    y: null,
  };

  public tooltips = {
    negative: {
      title: this.$t("ui.shared.global.metrics.correlation.negative_description"),
      customClass: "tooltip-xl text-left",
    },
    positive: {
      title: this.$t("ui.shared.global.metrics.correlation.positive_description"),
      customClass: "tooltip-xl text-left",
    },
  };

  public get periods(): ChartApi.CorrelationPeriod[] {
    const today = endOfDay(new Date());

    return [
      {
        value: "3m",
        label: this.$t("ui.shared.global.metrics.periods.month.abbr", { period: 3 }) as string,
        disabled: isAfter(this.youngest, subMonths(today, 3)),
      },
      {
        value: "6m",
        label: this.$t("ui.shared.global.metrics.periods.month.abbr", { period: 6 }) as string,
        disabled: isAfter(this.youngest, subMonths(today, 6)),
      },
      { value: "ytd", label: this.$t("ui.shared.global.metrics.periods.ytd") as string, disabled: false },
      {
        value: "1y",
        label: this.$t("ui.shared.global.metrics.periods.year.abbr", { period: 1 }) as string,
        disabled: isAfter(this.youngest, subYears(today, 1)),
      },
      {
        value: "3y",
        label: this.$t("ui.shared.global.metrics.periods.year.abbr", { period: 3 }) as string,
        disabled: isAfter(this.youngest, subYears(today, 3)),
      },
      {
        value: "5y",
        label: this.$t("ui.shared.global.metrics.periods.year.abbr", { period: 5 }) as string,
        disabled: isAfter(this.youngest, subYears(today, 5)),
      },
      { value: "max", label: this.$t("ui.shared.global.metrics.periods.all") as string, disabled: false },
    ];
  }

  @Watch("selectedPeriod")
  public onSelectedPeriodChanged(): void {
    this.calculateCorrelation();
  }

  @Watch("benchmarks")
  public onBenchmarksChanged(): void {
    this.calculateCorrelation();
  }

  @Watch("isins")
  public onIsinsChanged(): void {
    this.calculateCorrelation();
  }

  public isBenchmark(item: ChartApi.CorrelationData): boolean {
    if (this.benchmarks === null) return false;

    return this.benchmarks.some(
      (benchmark) => item.identifier === benchmark.identifier || item.name === benchmark.portfolio?.name
    );
  }

  public nameStyleRow(item: ChartApi.CorrelationData, index: number): string {
    if (this.isBenchmark(item)) {
      return `border-left: 2px solid ${
        chartService.colors.default[index - this.isins.length + 1]
      }; padding-left: 0.5rem`;
    } else return "";
  }

  public nameStyleColumn(item: ChartApi.CorrelationData, index: number): string {
    if (this.isBenchmark(item)) {
      return `border-bottom: 2px solid ${
        chartService.colors.default[index - this.isins.length + 1]
      }; padding-bottom: 1.5rem`;
    } else return "";
  }

  public handlePeriodChanged(period: ChartApi.CorrelationPeriod): void {
    if (!period.disabled) this.selectedPeriod = period.value;
  }

  public handleCorrelationMouseover(correlation: ChartApi.Correlation): void {
    if (correlation.value) {
      this.hovering = correlation.index;
    } else this.hovering = { x: null, y: null };
  }

  public handleCorrelationMouseleave(): void {
    this.hovering = { x: null, y: null };
  }

  public async calculateCorrelation(): Promise<void> {
    this.errorMessage = "";
    this.calculating = true;

    try {
      const correlationResult = await correlationService.calculate(this.isins, this.benchmarks, {
        range: this.selectedPeriod,
      });

      this.correlationData = correlationResult.data;

      if (this.selectedPeriod === "max") this.youngest = correlationResult.youngest;
    } catch (e: any) {
      this.errorMessage = e.message;
    } finally {
      this.calculating = false;
    }
  }

  protected mounted(): void {
    this.calculateCorrelation();
  }
}
